import { reactive } from 'vue';
import { currentAuthor, type ChatAuthor } from './profile-card';
import { profileLocal } from './profiles/local';

/** The three conversations in the side chat. */
export type ChatChannel = 'ai' | 'team' | 'personal';

/** Channels a future sync may carry. Personal notes never leave this device. */
export type SharedChannel = 'ai' | 'team';

export interface ChatLine {
  id: string;
  text: string;
  at: number;
  /** Person shown next to the line. Empty name until a profile is filled in. */
  author: ChatAuthor;
  /** Another line in the same thread, or null. Kept after that line is deleted so a sync can still see the link. */
  replyTo: string | null;
}

/** An AI or team line, with what a future sync needs to tell copies apart. */
export interface SyncChatLine extends ChatLine {
  canvasId: string;
  channel: SharedChannel;
  /** pending = only on this device; acked = a future sync has confirmed a remote copy. */
  sync: 'pending' | 'acked';
}

/** A removed AI or team line a future sync still has to drop everywhere. */
export interface SyncChatDelete {
  id: string;
  canvasId: string;
  channel: SharedChannel;
  sync: 'pending';
}

const PERSONAL_PREFIX = 'vp-chat-personal:';
const SHARED_PREFIX = 'vp-chat-sync:';
const DELETE_PREFIX = 'vp-chat-sync-del:';
const MAX_LINES = 100;

/**
 * Future sync fills this in. `pull` returns lines written for this canvas elsewhere;
 * today nothing is connected, so it returns none. New local AI and team lines stay
 * available through `pendingChatSync` until that sync calls `ackChatSync`.
 */
export const chatSync: {
  pull(canvasId: string): Promise<SyncChatLine[]>;
  /** Ids removed elsewhere. Empty until a sync is connected. */
  pullDeletes(canvasId: string): Promise<string[]>;
} = {
  pull: () => Promise.resolve([]),
  pullDeletes: () => Promise.resolve([]),
};

/** The open canvas's three threads. Switching canvases replaces them. */
export const chatThreads = reactive<Record<ChatChannel, ChatLine[]>>({
  ai: [],
  team: [],
  personal: [],
});

let bound: string | null = null;

function authorOf(x: unknown): ChatAuthor {
  if (!x || typeof x !== 'object') return { id: '', name: '', avatar: '' };
  const a = x as Partial<ChatAuthor>;
  return {
    id: typeof a.id === 'string' ? a.id : '',
    name: typeof a.name === 'string' ? a.name : '',
    avatar: typeof a.avatar === 'string' ? a.avatar : '',
  };
}

function toLine(x: unknown): ChatLine | null {
  if (!x || typeof x !== 'object') return null;
  const line = x as Partial<ChatLine>;
  if (typeof line.id !== 'string' || typeof line.text !== 'string' || typeof line.at !== 'number') return null;
  return { id: line.id, text: line.text, at: line.at, author: authorOf(line.author), replyTo: typeof line.replyTo === 'string' ? line.replyTo : null };
}

function readList(key: string): unknown[] {
  try {
    const raw = profileLocal.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function readPersonal(canvasId: string): ChatLine[] {
  return readList(PERSONAL_PREFIX + canvasId).map(toLine).filter((l): l is ChatLine => !!l).slice(-MAX_LINES);
}

function readShared(canvasId: string): SyncChatLine[] {
  const out: SyncChatLine[] = [];
  for (const x of readList(SHARED_PREFIX + canvasId)) {
    const line = toLine(x);
    if (!line) continue;
    const raw = x as Partial<SyncChatLine>;
    if (raw.channel !== 'ai' && raw.channel !== 'team') continue;
    out.push({ ...line, canvasId, channel: raw.channel, sync: raw.sync === 'acked' ? 'acked' : 'pending' });
  }
  return out.slice(-MAX_LINES * 2);
}

function readDeletes(canvasId: string): SyncChatDelete[] {
  const out: SyncChatDelete[] = [];
  for (const x of readList(DELETE_PREFIX + canvasId)) {
    if (!x || typeof x !== 'object') continue;
    const row = x as Partial<SyncChatDelete>;
    if (typeof row.id !== 'string' || (row.channel !== 'ai' && row.channel !== 'team')) continue;
    out.push({ id: row.id, canvasId, channel: row.channel, sync: 'pending' });
  }
  return out;
}

function write(key: string, value: unknown): void {
  try {
    profileLocal.setItem(key, JSON.stringify(value));
  } catch {
    /* storage full or blocked */
  }
}

function trim(list: ChatLine[]): void {
  if (list.length > MAX_LINES) list.splice(0, list.length - MAX_LINES);
}

function show(canvasId: string): void {
  const shared = readShared(canvasId);
  const asLine = ({ id, text, at, author, replyTo }: SyncChatLine): ChatLine => ({ id, text, at, author, replyTo });
  const next: Record<ChatChannel, ChatLine[]> = {
    ai: shared.filter((l) => l.channel === 'ai').map(asLine),
    team: shared.filter((l) => l.channel === 'team').map(asLine),
    personal: readPersonal(canvasId),
  };
  for (const channel of ['ai', 'team', 'personal'] as const) {
    trim(next[channel]);
    chatThreads[channel].splice(0, chatThreads[channel].length, ...next[channel]);
  }
}

/** Keep sync flags already on disk; lines just merged from a remote copy are acked. */
function saveShared(canvasId: string, acked?: ReadonlySet<string>): void {
  const prev = new Map(readShared(canvasId).map((l) => [l.id, l.sync]));
  const lines: SyncChatLine[] = [];
  for (const channel of ['ai', 'team'] as const) {
    for (const line of chatThreads[channel]) {
      lines.push({ ...line, canvasId, channel, sync: acked?.has(line.id) ? 'acked' : (prev.get(line.id) ?? 'pending') });
    }
  }
  write(SHARED_PREFIX + canvasId, lines);
}

function savePersonal(canvasId: string): void {
  write(PERSONAL_PREFIX + canvasId, chatThreads.personal);
}

function keysWith(prefix: string): string[] {
  const keys: string[] = [];
  try {
    for (let i = 0; i < profileLocal.length; i++) {
      const key = profileLocal.key(i);
      if (key?.startsWith(prefix)) keys.push(key);
    }
  } catch {
    /* storage blocked */
  }
  return keys;
}

/** AI and team lines still only on this device, across every canvas. */
export function pendingChatSync(): SyncChatLine[] {
  const out: SyncChatLine[] = [];
  for (const key of keysWith(SHARED_PREFIX)) {
    const canvasId = key.slice(SHARED_PREFIX.length);
    for (const line of readShared(canvasId)) if (line.sync === 'pending') out.push(line);
  }
  return out;
}

/** Deletions of AI and team lines a future sync has not applied yet. */
export function pendingChatDeletes(): SyncChatDelete[] {
  const out: SyncChatDelete[] = [];
  for (const key of keysWith(DELETE_PREFIX)) out.push(...readDeletes(key.slice(DELETE_PREFIX.length)));
  return out;
}

/** A future sync calls this after the remote copy of these lines exists. */
export function ackChatSync(ids: string[]): void {
  const want = new Set(ids);
  if (!want.size) return;
  for (const key of keysWith(SHARED_PREFIX)) {
    const canvasId = key.slice(SHARED_PREFIX.length);
    const lines = readShared(canvasId);
    let changed = false;
    for (const line of lines) {
      if (want.has(line.id) && line.sync !== 'acked') {
        line.sync = 'acked';
        changed = true;
      }
    }
    if (changed) write(key, lines);
    const deletes = readDeletes(canvasId).filter((row) => !want.has(row.id));
    if (deletes.length !== readDeletes(canvasId).length) write(DELETE_PREFIX + canvasId, deletes);
  }
}

function mergeRemote(canvasId: string, remote: SyncChatLine[]): void {
  const acked = new Set<string>();
  for (const channel of ['ai', 'team'] as const) {
    const have = new Set(chatThreads[channel].map((l) => l.id));
    for (const line of remote) {
      if (line.canvasId !== canvasId || line.channel !== channel || have.has(line.id)) continue;
      chatThreads[channel].push({ id: line.id, text: line.text, at: line.at, author: authorOf(line.author), replyTo: line.replyTo ?? null });
      have.add(line.id);
      acked.add(line.id);
    }
    chatThreads[channel].sort((a, b) => a.at - b.at);
    trim(chatThreads[channel]);
  }
  if (acked.size) saveShared(canvasId, acked);
}

/** Show this canvas's own threads, then fold in anything a future sync already has. */
export async function openCanvasChat(canvasId: string): Promise<void> {
  bound = canvasId;
  show(canvasId);
  let remote: SyncChatLine[] = [];
  try {
    remote = await chatSync.pull(canvasId);
  } catch {
    remote = [];
  }
  if (bound === canvasId && remote.length) mergeRemote(canvasId, remote);
  let removed: string[] = [];
  try {
    removed = await chatSync.pullDeletes(canvasId);
  } catch {
    removed = [];
  }
  if (bound !== canvasId || !removed.length) return;
  const drop = new Set(removed);
  for (const channel of ['ai', 'team'] as const) {
    const next = chatThreads[channel].filter((line) => !drop.has(line.id));
    if (next.length !== chatThreads[channel].length) chatThreads[channel].splice(0, chatThreads[channel].length, ...next);
  }
  saveShared(canvasId);
}

export function postChat(channel: ChatChannel, text: string, replyTo: string | null = null): void {
  const canvasId = bound;
  const trimmed = text.trim().slice(0, 4000);
  if (!canvasId || !trimmed) return;
  const line: ChatLine = {
    id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    text: trimmed,
    at: Date.now(),
    author: currentAuthor(),
    replyTo,
  };
  const list = chatThreads[channel];
  list.push(line);
  trim(list);
  if (channel === 'personal') savePersonal(canvasId);
  else saveShared(canvasId);
}

/** Remove one line. An AI or team line that already reached a future sync is remembered as a deletion. */
export function deleteChat(channel: ChatChannel, id: string): void {
  const canvasId = bound;
  if (!canvasId) return;
  const list = chatThreads[channel];
  const index = list.findIndex((line) => line.id === id);
  if (index < 0) return;
  list.splice(index, 1);
  if (channel === 'personal') {
    savePersonal(canvasId);
    return;
  }
  const synced = readShared(canvasId).some((line) => line.id === id && line.sync === 'acked');
  saveShared(canvasId);
  if (!synced) return;
  const deletes = readDeletes(canvasId).filter((row) => row.id !== id);
  deletes.push({ id, canvasId, channel, sync: 'pending' });
  write(DELETE_PREFIX + canvasId, deletes);
}

/** A home-feed line. `canvasId` is set only when the writer picked a canvas with `!`. */
export interface InboxLine extends ChatLine {
  canvasId: string | null;
}

export type InboxChannel = 'ai' | 'personal';

interface InboxStored extends InboxLine {
  channel: InboxChannel;
  sync: 'pending' | 'acked';
}

const INBOX_KEY = 'vp-chat-inbox';
const INBOX_DELETE_KEY = 'vp-chat-inbox-del';

/** AI and personal lines written from the feed when no canvas is open. Team does not exist there. */
export const inboxThreads = reactive<Record<InboxChannel, InboxLine[]>>({
  ai: [],
  personal: [],
});

/**
 * Future sync of lines that are not tied to a canvas.
 * A line tied to a canvas is stored with that canvas and rides `pendingChatSync` instead.
 */
export const inboxSync: {
  pull(): Promise<InboxStored[]>;
  pullDeletes(): Promise<string[]>;
} = {
  pull: () => Promise.resolve([]),
  pullDeletes: () => Promise.resolve([]),
};

function readInbox(): InboxStored[] {
  const out: InboxStored[] = [];
  for (const x of readList(INBOX_KEY)) {
    const line = toLine(x);
    if (!line) continue;
    const raw = x as Partial<InboxStored>;
    if (raw.channel !== 'ai' && raw.channel !== 'personal') continue;
    out.push({
      ...line,
      channel: raw.channel,
      canvasId: typeof raw.canvasId === 'string' && raw.canvasId ? raw.canvasId : null,
      sync: raw.sync === 'acked' ? 'acked' : 'pending',
    });
  }
  return out.slice(-MAX_LINES * 2);
}

function readInboxDeletes(): { id: string }[] {
  const out: { id: string }[] = [];
  for (const x of readList(INBOX_DELETE_KEY)) {
    if (!x || typeof x !== 'object') continue;
    const id = (x as { id?: unknown }).id;
    if (typeof id === 'string') out.push({ id });
  }
  return out;
}

function saveInbox(acked?: ReadonlySet<string>): void {
  const prev = new Map(readInbox().map((l) => [l.id, l.sync]));
  const lines: InboxStored[] = [];
  for (const channel of ['ai', 'personal'] as const) {
    for (const line of inboxThreads[channel]) {
      const linked = channel === 'ai' && !!line.canvasId;
      lines.push({
        ...line,
        channel,
        sync: channel === 'personal' || linked ? 'acked' : acked?.has(line.id) ? 'acked' : (prev.get(line.id) ?? 'pending'),
      });
    }
  }
  write(INBOX_KEY, lines);
}

function showInbox(): void {
  const rows = readInbox();
  for (const channel of ['ai', 'personal'] as const) {
    const next = rows
      .filter((row) => row.channel === channel)
      .map(({ id, text, at, author, replyTo, canvasId }) => ({ id, text, at, author, replyTo, canvasId }));
    trim(next);
    inboxThreads[channel].splice(0, inboxThreads[channel].length, ...next);
  }
}

/** AI lines from the feed that are not tied to a canvas and still only on this device. */
export function pendingInboxSync(): InboxStored[] {
  return readInbox().filter((line) => line.channel === 'ai' && !line.canvasId && line.sync === 'pending');
}

/** Deletions of those unbound lines a future sync has not applied yet. */
export function pendingInboxDeletes(): { id: string }[] {
  return readInboxDeletes();
}

export function ackInboxSync(ids: string[]): void {
  const want = new Set(ids);
  if (!want.size) return;
  const lines = readInbox();
  let changed = false;
  for (const line of lines) {
    if (want.has(line.id) && line.sync !== 'acked') {
      line.sync = 'acked';
      changed = true;
    }
  }
  if (changed) write(INBOX_KEY, lines);
  const deletes = readInboxDeletes().filter((row) => !want.has(row.id));
  if (deletes.length !== readInboxDeletes().length) write(INBOX_DELETE_KEY, deletes);
}

function appendToCanvas(canvasId: string, channel: InboxChannel, line: ChatLine): void {
  const plain: ChatLine = { id: line.id, text: line.text, at: line.at, author: line.author, replyTo: line.replyTo };
  if (bound === canvasId) {
    chatThreads[channel].push(plain);
    trim(chatThreads[channel]);
    if (channel === 'personal') savePersonal(canvasId);
    else saveShared(canvasId);
    return;
  }
  if (channel === 'personal') {
    const list = readPersonal(canvasId);
    list.push(plain);
    trim(list);
    write(PERSONAL_PREFIX + canvasId, list);
    return;
  }
  const shared = readShared(canvasId);
  shared.push({ ...plain, canvasId, channel, sync: 'pending' });
  write(SHARED_PREFIX + canvasId, shared.slice(-MAX_LINES * 2));
}

function removeFromCanvas(canvasId: string, channel: InboxChannel, id: string): void {
  if (bound === canvasId) {
    const list = chatThreads[channel];
    const index = list.findIndex((line) => line.id === id);
    if (index >= 0) list.splice(index, 1);
    if (channel === 'personal') {
      savePersonal(canvasId);
      return;
    }
    const synced = readShared(canvasId).some((line) => line.id === id && line.sync === 'acked');
    saveShared(canvasId);
    if (!synced) return;
    const deletes = readDeletes(canvasId).filter((row) => row.id !== id);
    deletes.push({ id, canvasId, channel, sync: 'pending' });
    write(DELETE_PREFIX + canvasId, deletes);
    return;
  }
  if (channel === 'personal') {
    write(PERSONAL_PREFIX + canvasId, readPersonal(canvasId).filter((line) => line.id !== id));
    return;
  }
  const shared = readShared(canvasId);
  const synced = shared.some((line) => line.id === id && line.sync === 'acked');
  write(SHARED_PREFIX + canvasId, shared.filter((line) => line.id !== id));
  if (!synced) return;
  const deletes = readDeletes(canvasId).filter((row) => row.id !== id);
  deletes.push({ id, canvasId, channel, sync: 'pending' });
  write(DELETE_PREFIX + canvasId, deletes);
}

/** Show the feed's own threads, outside any open canvas. */
export async function openInboxChat(): Promise<void> {
  showInbox();
  let remote: InboxStored[] = [];
  try {
    remote = await inboxSync.pull();
  } catch {
    remote = [];
  }
  if (!remote.length) return;
  const acked = new Set<string>();
  for (const channel of ['ai', 'personal'] as const) {
    const have = new Set(inboxThreads[channel].map((line) => line.id));
    for (const line of remote) {
      if (line.channel !== channel || line.canvasId || have.has(line.id)) continue;
      inboxThreads[channel].push({ id: line.id, text: line.text, at: line.at, author: authorOf(line.author), replyTo: line.replyTo ?? null, canvasId: null });
      acked.add(line.id);
    }
    inboxThreads[channel].sort((a, b) => a.at - b.at);
    trim(inboxThreads[channel]);
  }
  if (acked.size) saveInbox(acked);
}

export function postInbox(channel: InboxChannel, text: string, replyTo: string | null = null, canvasId: string | null = null): void {
  const trimmed = text.trim().slice(0, 4000);
  if (!trimmed) return;
  const line: InboxLine = {
    id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    text: trimmed,
    at: Date.now(),
    author: currentAuthor(),
    replyTo,
    canvasId,
  };
  const list = inboxThreads[channel];
  list.push(line);
  trim(list);
  saveInbox();
  if (canvasId) appendToCanvas(canvasId, channel, line);
}

export function deleteInbox(channel: InboxChannel, id: string): void {
  const list = inboxThreads[channel];
  const index = list.findIndex((line) => line.id === id);
  if (index < 0) return;
  const line = list[index]!;
  const stored = readInbox().find((row) => row.id === id);
  list.splice(index, 1);
  saveInbox();
  if (line.canvasId) {
    removeFromCanvas(line.canvasId, channel, id);
    return;
  }
  if (channel === 'ai' && stored?.sync === 'acked') {
    const deletes = readInboxDeletes().filter((row) => row.id !== id);
    deletes.push({ id });
    write(INBOX_DELETE_KEY, deletes);
  }
}

/** Saved lines of one canvas; `canvasId: null` is the feed's own lines not tied to a canvas. */
export interface ChatStorageEntry {
  canvasId: string | null;
  count: number;
}

/** What chats keep on this device, per canvas. Entries without lines are left out. */
export function chatStorageSummary(): ChatStorageEntry[] {
  const counts = new Map<string, number>();
  const add = (id: string, n: number): void => {
    if (n) counts.set(id, (counts.get(id) ?? 0) + n);
  };
  for (const key of keysWith(PERSONAL_PREFIX)) {
    const id = key.slice(PERSONAL_PREFIX.length);
    add(id, readPersonal(id).length);
  }
  for (const key of keysWith(SHARED_PREFIX)) {
    const id = key.slice(SHARED_PREFIX.length);
    add(id, readShared(id).length);
  }
  const out: ChatStorageEntry[] = [...counts].map(([canvasId, count]) => ({ canvasId, count }));
  const unbound = readInbox().filter((line) => !line.canvasId).length;
  if (unbound) out.push({ canvasId: null, count: unbound });
  return out;
}

/** Remove saved lines of one canvas. Lines a future sync already has are remembered as deletions. */
export function clearCanvasChat(canvasId: string): void {
  const acked = readShared(canvasId).filter((line) => line.sync === 'acked');
  const deletes = readDeletes(canvasId).filter((row) => !acked.some((line) => line.id === row.id));
  for (const line of acked) deletes.push({ id: line.id, canvasId, channel: line.channel, sync: 'pending' });
  try {
    profileLocal.removeItem(PERSONAL_PREFIX + canvasId);
    profileLocal.removeItem(SHARED_PREFIX + canvasId);
  } catch {
    /* storage blocked */
  }
  if (deletes.length) write(DELETE_PREFIX + canvasId, deletes);
  if (bound === canvasId) for (const channel of ['ai', 'team', 'personal'] as const) chatThreads[channel].splice(0);
  const rows = readInbox();
  const left = rows.filter((line) => line.canvasId !== canvasId);
  if (left.length !== rows.length) write(INBOX_KEY, left);
  for (const channel of ['ai', 'personal'] as const) {
    const next = inboxThreads[channel].filter((line) => line.canvasId !== canvasId);
    if (next.length !== inboxThreads[channel].length) inboxThreads[channel].splice(0, inboxThreads[channel].length, ...next);
  }
}

/** Remove the feed's own lines that are not tied to a canvas. */
export function clearInboxChat(): void {
  const rows = readInbox();
  const gone = rows.filter((line) => !line.canvasId);
  if (!gone.length) return;
  const deletes = readInboxDeletes();
  for (const line of gone) if (line.channel === 'ai' && line.sync === 'acked' && !deletes.some((row) => row.id === line.id)) deletes.push({ id: line.id });
  write(INBOX_KEY, rows.filter((line) => !!line.canvasId));
  if (deletes.length) write(INBOX_DELETE_KEY, deletes);
  for (const channel of ['ai', 'personal'] as const) {
    const next = inboxThreads[channel].filter((line) => !!line.canvasId);
    inboxThreads[channel].splice(0, inboxThreads[channel].length, ...next);
  }
}

/** Remove every saved chat line on this device. */
export function clearAllChats(): void {
  for (const entry of chatStorageSummary()) {
    if (entry.canvasId) clearCanvasChat(entry.canvasId);
  }
  clearInboxChat();
}

/** Drop a canvas's threads, including anything still waiting for sync. */
export function forgetCanvasChat(canvasId: string): void {
  try {
    profileLocal.removeItem(PERSONAL_PREFIX + canvasId);
    profileLocal.removeItem(SHARED_PREFIX + canvasId);
    profileLocal.removeItem(DELETE_PREFIX + canvasId);
  } catch {
    /* storage blocked */
  }
  if (bound === canvasId) {
    bound = null;
    for (const channel of ['ai', 'team', 'personal'] as const) chatThreads[channel].splice(0);
  }
  const rows = readInbox();
  const left = rows.filter((line) => line.canvasId !== canvasId);
  if (left.length !== rows.length) write(INBOX_KEY, left);
  for (const channel of ['ai', 'personal'] as const) {
    const next = inboxThreads[channel].filter((line) => line.canvasId !== canvasId);
    if (next.length !== inboxThreads[channel].length) inboxThreads[channel].splice(0, inboxThreads[channel].length, ...next);
  }
}
